import { useAuth } from '../../auth/AuthContext'
import { isStaffOnlyScreen, SCREEN_FEATURE_FLAG, type ScreenId } from '../../auth/screenAccess'
import { type Message, type Proposal } from './types'
import { notifBtn, notifGhostBtn } from './styles'
import { errorDisplayText, renderMarkdown } from './helpers'
import { ProposalCard } from './ProposalCard'

export function MessageBubble({ message, onExecute, onDismiss, onNavigate, onAskFollowup, onDismissNotification, isActioned, notificationCard = false }: {
  message: Message
  onExecute: (idx: number, p: Proposal) => void
  onDismiss: (idx: number) => void
  onNavigate: (screen: string, notifId?: string) => void
  onAskFollowup: (text: string) => void
  onDismissNotification: (notifId: string) => void
  isActioned: boolean
  notificationCard?: boolean
}) {
  const { user, hasFeature } = useAuth()
  const isUser = message.role === 'user'
  const notif = message.notification
  const card = notificationCard && !!notif
  // Notification messages get a tinted background so they read as alerts
  const bubbleBg = isUser
    ? 'var(--parkify-accent)'
    : notif?.severity === 'high'
      ? 'rgba(21,128,61,0.10)'   // green tint — revenue opportunity
      : notif?.severity === 'warning'
        ? 'rgba(241,101,33,0.12)'
        : notif
          ? 'color-mix(in srgb, var(--parkify-accent) 10%, transparent)'
          : 'var(--color-white)'
  const bubbleBorder = !isUser && notif
    ? `1px solid ${notif.severity === 'high' ? 'rgba(21,128,61,0.45)' : notif.severity === 'warning' ? 'rgba(241,101,33,0.35)' : 'color-mix(in srgb, var(--parkify-accent) 30%, transparent)'}`
    : isUser
      ? 'none'
      : '1px solid var(--color-brand-border)'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
      <div style={card ? {
        width: '100%', boxSizing: 'border-box', minWidth: 0,
        background: 'var(--color-white)', border: '1px solid var(--color-brand-border)',
        borderRadius: 12, padding: '14px 16px',
      } : { minWidth: 0, maxWidth: '86%' }}>
        <div style={{
          background: bubbleBg,
          color: isUser ? '#fff' : 'var(--color-black)',
          padding: '11px 15px',
          borderRadius: 14,
          borderBottomLeftRadius: isUser ? 14 : 4,
          borderBottomRightRadius: isUser ? 4 : 14,
          fontSize: 15,
          fontWeight: isUser ? 500 : 400,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          border: bubbleBorder,
          boxShadow: !isUser && !notif ? '0 1px 3px color-mix(in srgb, var(--parkify-accent) 8%, transparent)' : 'none',
          ...(card ? { background: 'transparent', border: 'none', padding: 0, borderRadius: 0, fontSize: 12.5, fontWeight: 500, color: 'var(--color-gray-700)' } : {}),
        }}>
          {notif && (
            <div style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase',
              color: notif.severity === 'high' ? (card ? 'var(--parkify-accent)' : '#62c584') : notif.severity === 'warning' ? '#c2410c' : 'var(--parkify-accent)',
              marginBottom: 4,
            }}>
              {card ? (notif.severity === 'high' ? 'Opportunity' : notif.severity === 'warning' ? 'Heads up' : 'Update') : notif.severity === 'high'
                ? '💡 Revenue opportunity'
                : notif.severity === 'warning' ? '⚠️ Heads up' : '🔔 Update'}
            </div>
          )}
          {card && notif ? <>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-black)', letterSpacing: '-0.2px', marginBottom: 4 }}>{notif.headline}</div>
            {renderMarkdown(notif.body)}
          </> : renderMarkdown(errorDisplayText(message.content))}
        </div>
        {notif?.proposal && (
          <div style={{ marginTop: 8 }}>
            <ProposalCard
              proposal={notif.proposal}
              status={message.proposalStatus?.[0] ?? 'pending'}
              error={message.proposalError?.[0]}
              onExecute={() => onExecute(0, notif.proposal!)}
              onDismiss={() => onDismiss(0)}
            />
          </div>
        )}
        {notif && (
          <div style={{ display: 'flex', gap: card ? 8 : 6, marginTop: card ? 10 : 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Avery's notification CTAs are generated server-side and can point
                at a staff-only screen (a chargeback card offering "Open
                disputes"). Rendering it for a client-scoped operator gives them a
                button that App.tsx's guard bounces straight back — hide it
                instead. The card itself still shows, so they keep the heads-up.
                Same for a screen the tenant has switched off (its feature flag,
                the gate App.tsx applies too). */}
            {notif.cta?.screen && !isStaffOnlyScreen(notif.cta.screen as ScreenId, user)
              && !(SCREEN_FEATURE_FLAG[notif.cta.screen as ScreenId] && !hasFeature(SCREEN_FEATURE_FLAG[notif.cta.screen as ScreenId]!)) && (
              <button onClick={() => onNavigate(notif.cta!.screen!, notif.id)} style={card ? { ...notifGhostBtn, color: 'var(--parkify-accent)' } : notifBtn}>
                {notif.cta.label} →
              </button>
            )}
            <button
              onClick={() => onAskFollowup(`Tell me more about: ${notif.headline}`)}
              style={notifGhostBtn}
            >
              Ask Avery
            </button>
            {!isActioned && !notif.proposal && (
              <button
                onClick={() => onDismissNotification(notif.id)}
                style={notifGhostBtn}
                title="Dismiss this recommendation"
              >
                Dismiss
              </button>
            )}
            {isActioned && (
              <span style={{ fontSize: 10, color: 'var(--color-gray-500)', marginLeft: 4 }}>✓ acknowledged</span>
            )}
          </div>
        )}
        {message.toolTrace && message.toolTrace.length > 0 && (
          <details style={{ marginTop: 6, fontSize: 10, color: 'var(--color-gray-500)' }}>
            <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
              🔍 {message.toolTrace.length} tool call{message.toolTrace.length === 1 ? '' : 's'}
            </summary>
            <ul style={{ margin: '4px 0 0', padding: '0 0 0 14px' }}>
              {message.toolTrace.map((t, i) => (
                <li key={i} style={{ fontFamily: 'monospace', fontSize: 10, marginBottom: 1, wordBreak: 'break-all' }}>
                  <strong>{t.tool}</strong>({JSON.stringify(t.input)})
                </li>
              ))}
            </ul>
          </details>
        )}
        {message.proposals && message.proposals.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {message.proposals.map((p, i) => (
              <ProposalCard
                key={i}
                proposal={p}
                status={message.proposalStatus?.[i] ?? 'pending'}
                error={message.proposalError?.[i]}
                onExecute={() => onExecute(i, p)}
                onDismiss={() => onDismiss(i)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
